import {setBlockType, wrapIn} from 'prosemirror-commands';
import {isInCard, isInCardContent, isInCardHeading, isInList, isInMessage,} from './helper/nodeSelection';
import {toggleList} from './plugins/commands';
import {uploadFile} from './helper/file';
import FormatHeader1Svg from '@mdi/svg/svg/format-header-1.svg';
import FormatHeader2Svg from '@mdi/svg/svg/format-header-2.svg';
import FormatHeader3Svg from '@mdi/svg/svg/format-header-3.svg';
import FormatClearSvg from '@mdi/svg/svg/format-clear.svg';
import FormatQuoteCloseSvg from '@mdi/svg/svg/format-quote-close.svg';
import CodeTagsSvg from '@mdi/svg/svg/code-tags.svg';
import MinusSvg from '@mdi/svg/svg/minus.svg';
import FormatListBulletedSvg from '@mdi/svg/svg/format-list-bulleted.svg';
import FormatListNumberedSvg from '@mdi/svg/svg/format-list-numbered.svg';
import FormatListChecksSvg from '@mdi/svg/svg/format-list-checks.svg';
import TableSvg from '@mdi/svg/svg/table.svg';
import GridSvg from '@mdi/svg/svg/grid.svg';
import FileUploadOutlineSvg from '@mdi/svg/svg/file-upload-outline.svg';
import EmoticonHappyOutlineSvg from '@mdi/svg/svg/emoticon-happy-outline.svg';
import FunctionVariantSvg from '@mdi/svg/svg/function-variant.svg';
import CardTextOutlineSvg from '@mdi/svg/svg/card-text-outline.svg';
import ChartGanttSvg from '@mdi/svg/svg/chart-gantt.svg';
import GraphSvg from '@mdi/svg/svg/graph.svg';
import SitemapSvg from '@mdi/svg/svg/sitemap.svg';
import FormTextboxSvg from '@mdi/svg/svg/form-textbox.svg';
import FormTextboxPasswordSvg from '@mdi/svg/svg/form-textbox-password.svg';
import FormTextareaSvg from '@mdi/svg/svg/form-textarea.svg';
import FormSelectSvg from '@mdi/svg/svg/form-select.svg';
import ListBoxOutlineSvg from '@mdi/svg/svg/list-box-outline.svg';
import ViewColumnOutlineSvg from '@mdi/svg/svg/view-column-outline.svg';
import CheckboxMultipleBlankCircleOutlineSvg from '@mdi/svg/svg/checkbox-multiple-blank-circle-outline.svg';
import CheckboxMultipleOutlineSvg from '@mdi/svg/svg/checkbox-multiple-outline.svg';
import TableOfContentsSvg from '@mdi/svg/svg/table-of-contents.svg';
import FileMultipleOutlineSvg from '@mdi/svg/svg/file-multiple-outline.svg';
import CalendarEditOutlineSvg from "@mdi/svg/svg/calendar-edit-outline.svg";
import {NodeSelection, TextSelection} from 'prosemirror-state';
import {DiagramEditor} from './helper/DiagramEditor';
import {issueNodes, issuesNodes} from './templates/issueNodes';
import {issueTypeNodes} from './templates/issueTypeNodes';
import {issuePriorityNodes} from './templates/issuePriorityNodes';
import {issueStatusNodes} from './templates/issueStatusNodes';
import {issueFormNodes} from './templates/issueFormNodes';
import {pageNodes, pagesNodes} from './templates/pageNodes';
import {countryNodes} from './templates/countryNodes';
import {issueResolutionNodes} from './templates/issueResolutionNodes';
import {userNodes, usersNodes} from './templates/userNodes';
import {issueSideBarNodes} from './templates/issueSideBarNodes';
import {tasksBoardNodes} from './templates/tasksBoardNodes';
import {tasksTableNodes} from './templates/tasksTableNodes';
import {parseDate} from "chrono-node";
import {createRelativeTime} from "../Common/createRelativeTime";
import CalendarClockOutlinesSvg from "@mdi/svg/svg/calendar-clock-outline.svg";
import {upperFirst} from "lodash/string";

export const buildAtCommands = view => text => {
    const parsedDate = parseDate(text);
    const settings = {
        datetime: (parsedDate || new Date()).toISOString(),
        format: 'datetime',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    };

    return createRelativeTime(settings).then(relativeTimeText => [{
        title: parsedDate ? upperFirst(text) : 'Now',
        description: relativeTimeText,
        image: CalendarClockOutlinesSvg,
        category: 'Date',
        action: () => {
            const relativeTime = view.state.schema.marks.relative_time.create(settings);
            const node = view.state.schema.text(relativeTimeText).mark([relativeTime]);
            view.dispatch(view.state.tr.replaceSelectionWith(node, false))
        }
    }]);
}

export const buildCommands = (view, nodeCommands, fileUploadUrl) => [
    {
        title: 'Heading 1',
        description: 'Big section heading.',
        image: FormatHeader1Svg,
        category: 'Basic',
        action: () => setBlockType(view.state.schema.nodes.heading, {level: 1})(
            view.state, view.dispatch),
        disabled: () => isInList(view.state) || (isInCardContent(view.state) && !isInCardHeading(view.state)),
    },
    {
        title: 'Heading 2',
        description: 'Medium section heading.',
        image: FormatHeader2Svg,
        category: 'Basic',
        action: () => setBlockType(view.state.schema.nodes.heading, {level: 2})(
            view.state, view.dispatch),
        disabled: () => isInList(view.state) || (isInCardContent(view.state) && !isInCardHeading(view.state)),
    },
    {
        title: 'Heading 3',
        description: 'Small section heading.',
        image: FormatHeader3Svg,
        category: 'Basic',
        action: () => setBlockType(view.state.schema.nodes.heading, {level: 3})(
            view.state, view.dispatch),
        disabled: () => isInList(view.state) || (isInCardContent(view.state) && !isInCardHeading(view.state)),
    },
    {
        title: 'Clear formatting',
        description: 'Just start writing plain text.',
        image: FormatClearSvg,
        category: 'Basic',
        action: () => setBlockType(view.state.schema.nodes.paragraph)(view.state,
            view.dispatch),
        disabled: () => isInList(view.state),
    },
    {
        title: 'Quote',
        description: 'Capture a quote.',
        image: FormatQuoteCloseSvg,
        category: 'Basic',
        action: () => wrapIn(view.state.schema.nodes.blockquote)(view.state,
            view.dispatch),
        disabled: () => isInCardHeading(view.state) || isInCard(view.state)
    },
    {
        title: 'Code block',
        description: 'Capture a code block.',
        image: CodeTagsSvg,
        category: 'Media',
        action: () => nodeCommands.createCodeBlock(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Divider',
        description: 'Visually divide block.',
        image: MinusSvg,
        category: 'Basic',
        action: () => view.dispatch(view.state.tr.replaceSelectionWith(
            view.state.schema.nodes.horizontal_rule.create()).scrollIntoView()),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Bullet list',
        description: 'Create a simple bulleted list.',
        image: FormatListBulletedSvg,
        category: 'Basic',
        action: () => toggleList(view.state.schema.nodes.bullet_list)(view.state,
            view.dispatch),
    },
    {
        title: 'Ordered list',
        description: 'Create a list with numbering.',
        image: FormatListNumberedSvg,
        category: 'Basic',
        action: () => toggleList(view.state.schema.nodes.ordered_list)(view.state,
            view.dispatch),
    },
    {
        title: 'To-do list',
        description: 'Track task with a to-do list.',
        keywords: 'task list',
        image: FormatListChecksSvg,
        category: 'Basic',
        action: () => nodeCommands.createTodoList(),
    },
    {
        title: 'Table',
        description: 'Add a table to this page.',
        image: TableSvg,
        category: 'Advanced',
        action: () => nodeCommands.createTable({rowsCount: 2, colsCount: 2}),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Grid',
        description: 'Add a grid to this page.',
        image: GridSvg,
        category: 'Advanced',
        action: () => nodeCommands.createGrid(),
    },
    {
        title: 'Card',
        description: 'Add card to this page.',
        image: CardTextOutlineSvg,
        category: 'Advanced',
        action: () => nodeCommands.createCard(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'File',
        description: 'Upload file.',
        keywords: 'document attachment image pdf doc word photo',
        image: FileUploadOutlineSvg,
        category: 'Media',
        action: () => {
            const modal = $.modal({
                content: `<file-attachment input="upload-files">
                            <div class="ui placeholder segment basic file action input">
                                <div class="ui icon header">
                                    <img class="ui image centered" src="${FileMultipleOutlineSvg}" alt="">
                                    <p>Drop files here.</p>
                                </div>
                                <label for="upload-files" class="ui primary button">Or browse files</label>
                                <input type="file" id="upload-files" multiple hidden="">
                            </div>
                       </file-attachment>`,
                preserveHTML: true,
                duration: 0,
                closeIcon: true,
                autofocus: false,
            }).modal('show');

            const uploadFileListener = e => {
                modal.modal('hide');

                const files = e.detail.attachments.map(dt => dt.file);
                if (files.length === 0) {
                    return false;
                }

                [...files].forEach(file => uploadFile(fileUploadUrl, file, view));

                document.removeEventListener('file-attachment-accepted', uploadFileListener)
            };

            document.addEventListener('file-attachment-accepted', uploadFileListener);
        },
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Emoji',
        description: 'Search for an emoji to place in text.',
        image: EmoticonHappyOutlineSvg,
        category: 'Basic',
        action: () => nodeCommands.triggerEmoji(),
    },
    {
        title: 'Equation block',
        description: 'Add a standalone math equation.',
        image: FunctionVariantSvg,
        category: 'Basic',
        action: () => nodeCommands.createMathBlock(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Message',
        description: 'A message displays information that explains nearby content.',
        keywords: 'call out',
        image: CardTextOutlineSvg,
        category: 'Basic',
        action: () => nodeCommands.createMessage(),
        disabled: () => isInMessage(view.state) || isInCard(view.state)
    },
    {
        title: 'Accordion',
        description: 'An accordion allows users to toggle the display of content.',
        keywords: 'disclosure expand collapse accordion',
        image: CardTextOutlineSvg,
        category: 'Advanced',
        action: () => nodeCommands.createDetails(),
        disabled: () => isInMessage(view.state) || isInCard(view.state)
    },
    {
        title: 'Mermaid diagram',
        description: 'Add a standalone Mermaid diagram.',
        image: ChartGanttSvg,
        category: 'Advanced',
        action: () => nodeCommands.createMermaid(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'PlantUML diagram',
        description: 'Add a standalone PlantUML diagram.',
        image: GraphSvg,
        category: 'Advanced',
        action: () => nodeCommands.createPlantUml(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'draw.io diagram',
        description: 'Add a draw.io/diagrams.net diagram.',
        image: SitemapSvg,
        category: 'Advanced',
        action: () => {
            const height = 100, width = 200;
            const defaultGraph = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}px" height="${height}px" viewBox="-0.5 -0.5 ${width} ${height}" content="&lt;mxfile host=&quot;embed.diagrams.net&quot; type=&quot;embed&quot;&gt;&lt;diagram name=&quot;Page-1&quot;&gt;ddHBEoIgEADQr+GOkJOdzerSyUNnRjZhBl0HabS+Ph0wY6wTy9uFZRbC82Y8W9GpK0owhFE5En4kjCXskE3LLE8v+13qobZahqIVSv2CgDToQ0voo0KHaJzuYqywbaFykQlrcYjL7mjirp2oYQNlJcxWb1o65TVL6eoX0LVaOic0ZBqxFAfolZA4fBEvCM8tovNRM+Zg5uEtc/HnTn+yn4dZaN2PA1Ow3j1toh/ixRs=&lt;/diagram&gt;&lt;/mxfile&gt;"><defs/><g/></svg>`;

            const file = new File(
                [defaultGraph],
                'draw.io diagram',
                {
                    type: 'image/svg+xml',
                });
            uploadFile(fileUploadUrl, file, view, true, pos => {
                const $pos = view.state.tr.doc.resolve(pos);
                view.dispatch(view.state.tr.setSelection(new NodeSelection($pos)));
                const {node: element} = view.domAtPos(pos);
                const imageEl = element?.querySelector('img');
                DiagramEditor.editElement(imageEl, view);
            });
        },
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Table of contents',
        description: 'Add a Table of Contents.',
        keywords: 'toc',
        image: TableOfContentsSvg,
        category: 'Advanced',
        action: () => nodeCommands.createTableOfContents(),
        disabled: () => isInCard(view.state)
    },
    {
        title: 'Single-line text',
        description: 'Insert single-line input text field.',
        keywords: 'input',
        image: FormTextboxSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_input.create()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Password',
        description: 'Insert input password field.',
        keywords: 'password secret',
        image: FormTextboxPasswordSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_input.create({type: 'password'})),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Paragraph text',
        description: 'Insert multi-line input text field.',
        keywords: 'textarea',
        image: FormTextareaSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_text_area.create()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Selection dropdown',
        description: 'Single selection dropdown field.',
        image: FormSelectSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_select.create()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Multiple selections dropdown',
        description: 'Multiple selections dropdown field.',
        image: FormSelectSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_select.create({multiple: true})),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Multiple choice field',
        description: 'Insert multiple choice field.',
        keywords: 'radio',
        image: CheckboxMultipleBlankCircleOutlineSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_choices.create()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Checkboxes',
        description: 'Insert checkboxes field.',
        image: CheckboxMultipleOutlineSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_checkboxes.create()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Calendar',
        description: 'Insert a calendar to allow users to select any date or time.',
        keywords: 'calendar input date time',
        image: CalendarEditOutlineSvg,
        category: 'Form',
        action: () => nodeCommands.createField(
            view.state.schema.nodes.form_calendar.createAndFill()),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Tasks board',
        description: 'Workflow tasks board.',
        keywords: 'sprint agile kanban board issues',
        image: ViewColumnOutlineSvg,
        category: 'Template',
        action: () => {
            const schema = view.state.schema;
            const offset = view.state.tr.selection.anchor + 1;
            const tr = view.state.tr.replaceSelectionWith(tasksBoardNodes(view)).scrollIntoView();
            const resolvedPos = tr.doc.resolve(offset);
            view.dispatch(tr.setSelection(TextSelection.near(resolvedPos)));
        },
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Tasks table',
        description: 'Workflow tasks table.',
        keywords: 'table list issues',
        image: TableSvg,
        category: 'Template',
        action: () => {
            const offset = view.state.tr.selection.anchor + 1;
            const tr = view.state.tr.replaceSelectionWith(tasksTableNodes(view)).scrollIntoView();
            const resolvedPos = tr.doc.resolve(offset);
            view.dispatch(tr.setSelection(TextSelection.near(resolvedPos)));
        },
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Issue form',
        description: 'Issue form to capture issue.',
        image: ListBoxOutlineSvg,
        category: 'Template',
        action: () => {
            const schema = view.state.schema;
            const offset = view.state.tr.selection.anchor + 1;
            const tr = view.state.tr.replaceSelectionWith(
                schema.nodes.grid.createAndFill({}, [
                    schema.nodes.grid_column.createAndFill({width: 12},
                        schema.nodes.grid.createAndFill({}, issueFormNodes(view))),
                    schema.nodes.grid_column.createAndFill({width: 4},
                        issueSideBarNodes(view)),
                ])).scrollIntoView();
            const resolvedPos = tr.doc.resolve(offset);
            view.dispatch(tr.setSelection(TextSelection.near(resolvedPos)));
        },
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Issue status selection dropdown',
        description: 'Issue status selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issueStatusNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Issue priority selection dropdown',
        description: 'Issue priority selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issuePriorityNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Issue type selection dropdown',
        description: 'Issue type selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issueTypeNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Issue resolution selection dropdown',
        description: 'Issue resolution selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issueResolutionNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Country selection dropdown',
        description: 'Country selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(countryNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'User selection dropdown',
        description: 'User selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(userNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'User multiple selection dropdown',
        description: 'User multiple selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(usersNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Page selection dropdown',
        description: 'Page selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(pageNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Page multiple selections dropdown',
        description: 'Page multiple selections dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(pagesNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Task selection dropdown',
        description: 'Task selection dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issueNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
    {
        title: 'Task multiple selections dropdown',
        description: 'Task multiple selections dropdown field.',
        image: FormSelectSvg,
        category: 'Template',
        action: () => nodeCommands.createField(issuesNodes(view)),
        disabled: () => !nodeCommands.createField ||
            typeof nodeCommands.createField !== 'function',
    },
];

export class buildDateCommands {
}